import UAParser from 'ua-parser-js';

import { log, logMethodCall } from '../utils/debug';
import { createNode, fadeIn, fadeOut } from '../utils/dom';
import { translate, charsetEscape, detectLanguage } from '../utils/translate';
import { topicChecked } from '../utils/topics';
import { detectPrompt } from '../utils/detect';
import { camelCaseToDash } from '../utils/strings';
import Event from '../const/event';
import './confirm.scss';

import {
  os,
  device,
  NOTIFICATION_ACCESS_TYPES,
  isNewerEdge,
  isChromeOrChromium,
  isEdge,
  isFirefox
} from '../utils/env';

import { addSentinelFocusTrap } from '../utils/sentinel';

const CONFIRM_SELECTOR = '.cleverpush-confirm';
const BACKDROP_SELECTOR = '.cleverpush-backdrop';
const CHECKED_TOPICS_AMOUNT_NEEDED = 0;
const REGEX_NEW_LINES = /\r?\n|\r/g;
const REGEX_SPACE = /\s/g;
const CONFIRM_CLASS_PREFIX = 'cleverpush-confirm';
const EMPTY_STRING = '';
const NEW_OPT_IN_THEMES = ['cascade-alert', 'clever-opt-in', 'midnight-harmony', 'nova-black', 'pulse-notify', 'swift-alert', 'vortex-notification', 'zenith-opt-in'];
const CLEVER_OPT_IN = 'cleverpush-confirm-clever-opt-in';
const WINDOWS = 'Windows';

let createdBy;

export default class Confirm {
  constructor(cleverpush, subscriptionManager) {
    this.autoHideBackdrop = true;
    this.cleverpush = cleverpush;
    this.config = cleverpush.config;
    this.subscriptionManager = subscriptionManager;
    this.id = document.querySelectorAll(CONFIRM_SELECTOR).length + 1;
    this.visible = false;
    this._hideWrapped = false; // Flag to prevent multiple wrapping of the hide method
    this.channelTopics = (this.config.channelTopics || []).filter((topic) => {
      let show = true;
      if (show && topic.matchPath && topic.matchPath.length) {
        show = new RegExp(topic.matchPath).test(window.parent.location.pathname);
      }
      if (show && topic.notMatchPath && topic.notMatchPath.length) {
        show = !(new RegExp(topic.notMatchPath).test(window.parent.location.pathname));
      }
      return show;
    });

    this.facebookEnabled = false;
    if (this.config.facebookPageId && this.config.facebookPageId.length && this.config.facebookCheckboxEnabled) {
      this.facebookEnabled = true;
      // generate an unique user reference
      this.facebookUserRef = this.subscriptionManager.storageManager.getFacebookUserRef();
      if (!this.facebookUserRef) {
        this.facebookUserRef = `${this.config.channelId}-`;
        this.facebookUserRef += Math.random().toString(36).slice(2);
      }
    }

    window.addEventListener('pagehide', () => {
      this.autoHideBackdrop = true;
      this.hide();
    });

    this.onTopicsAllowClick = this.onTopicsAllowClick.bind(this);
  }

  isLightColor(color) {
    if (!color || color === 'transparent') {
      return true;
    }

    color = color.toLowerCase().replace(/\s/g, '');

    let r;
    let g;
    let b;

    if (color.startsWith('#')) {
      const hex = color.substring(1);
      const bigint = parseInt(
        hex.length === 3 ? hex.split('').map((x) => x + x).join('') : hex,
        16
      );
      r = Math.floor(bigint / 65536) % 256;
      g = Math.floor(bigint / 256) % 256;
      b = bigint % 256;
    } else if (color.startsWith('rgb')) {
      const matches = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/i);
      if (matches) {
        [, r, g, b] = matches.map(Number);
      } else {
        return true;
      }
    } else {
      return true;
    }

    // YIQ-Formula
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    return brightness > 128;
  }

  resetBell(callback) {
    if (typeof callback === 'function') {
      this.bellResetCallback = callback;
    }
  }

  isNewOptIn = (confirmAlertTheme) => NEW_OPT_IN_THEMES.some((optIn) => confirmAlertTheme.includes(optIn));

  getArrowNode(type, config) {
    const deviceArrowConfig = () => {
      if (type === NOTIFICATION_ACCESS_TYPES.GRANT) {
        if (isChromeOrChromium()) {
          return {
            x: 347,
            y: 92,
            padding: 20
          };
        }
        if (isFirefox()) {
          return {
            x: 311,
            y: 148,
            padding: 20
          };
        }
      }
      if (type === NOTIFICATION_ACCESS_TYPES.DENIED) {
        if (isChromeOrChromium()) {
          return {
            x: 131,
            y: 0,
            padding: 0
          };
        }
        if (isFirefox()) {
          return {
            x: 60,
            y: 0,
            padding: 0
          };
        }
      } else if (type === NOTIFICATION_ACCESS_TYPES.SILENT) {
        if (isChromeOrChromium() || isEdge()) {
          return {
            x: window.innerWidth - 250,
            y: 0,
            padding: 0,
            class: 'reverse-arrow'
          };
        }
      }

      return null;
    };

    const i = type === 'own' ? config : ({ ...deviceArrowConfig(), ...config || {} });
    if (!i) {
      return null;
    }
    const text = i.textNode || document.querySelector('.cleverpush-backdrop-text h2');
    if (text && text.getBoundingClientRect) {
      const boundingRect = text.getBoundingClientRect();
      const o = i.position || {
        x: (window.innerWidth) / 2,
        y: (window.innerHeight - boundingRect.height) / 2
      };
      const r = Math.sqrt(Math.pow(i.x - o.x, 2) + Math.pow(i.y - o.y, 2));
      const S = Math.max(0.1 * r, 15);
      const s = i.x;
      const C = i.y + S + i.padding;
      const posX = o.x - 15;
      const posY = Math.max(o.y + 20 + boundingRect.height, C + 40);
      const l = posX - s;
      const h = posY - C;
      const _ = `M ${posX} ${posY} q ${-1 * l} ${h}, ${-1 * l} ${-1 * h}`;
      const c = `M ${s - S} ${C + S} q ${S} ${-3 * S}, ${2 * S} 0`;
      const className = (!!i.class && i.class) || 'cp-arrow';

      log.info('getArrowNode', {
        type,
        i,
        o,
        r,
        S,
        s,
        C,
        posX,
        posY,
        l,
        h,
        _,
        c
      });

      const N = `
      <div class="cleverpush-backdrop-arrow">
        <svg 
          width="${window.innerWidth}px"
          height="${window.innerHeight}px"
          class=${className}
          viewBox="0 0 ${window.innerWidth} ${window.innerHeight}"
          version="1.1" xmlns="http://www.w3.org/2000/svg"
        >
            <g stroke-width="5" stroke-linecap="round" fill="none" fill-rule="evenodd">
              <path d="${_}" id="tail" stroke="#FFFFFF"></path><path d="${c}" id="head" stroke="#FFFFFF"></path>
            </g>
         </svg>
        </div>
       </div>`;
      return createNode(N);
    }
    return null;
  }

  removeConfirmBox(force = false) {
    logMethodCall('removeConfirmBox');

    this.visible = false;
    const boxes = document.getElementsByClassName(CONFIRM_CLASS_PREFIX);
    if (boxes.length) {
      const box = boxes[boxes.length - 1];
      if (force || ((!this.withTopics || box.className.indexOf('cleverpush-topic-box') < 0) && box.className.indexOf('cleverpush-attributes-box') < 0)) {
        box.parentNode.removeChild(box);
      }
    }

    this.confirmBox = null;
  }

  removeBrandingBox() {
    logMethodCall('removeBrandingBox');

    this.visible = false;
    const boxes = document.getElementsByClassName('cleverpush-branding-box');
    if (boxes.length) {
      const elem = boxes[boxes.length - 1];
      elem.parentNode.removeChild(elem);
    }
  }

  hide(force = false) {
    logMethodCall('hide');

    if (this.autoHideBackdrop) {
      this.hideBackdrop();
    }
    this.removeConfirmBox(force);
    this.removeBrandingBox();
    if (createdBy) {
      createdBy.focus();
    }
  }

  onCloseClick(e) {
    logMethodCall('closeConfirmBox');

    e.preventDefault();

    const selectedTopics = this.getSelectedTopics();
    const hasTopics = this.hasTopicCheckboxes(true);
    if (this.config.confirmAlertRequireChannelTopics && !selectedTopics.length && hasTopics && this.withTopics && this.config.confirmAlertSelectTopicsLater) {
      alert(this.config.confirmAlertRequireChannelTopicsTitle || 'Bitte wähle mindestens eine Kategorie aus.');
      return;
    }

    this.hide(true);
    this.hideBackdrop();

    try {
      this.subscriptionManager.storageManager.setClosed();
      if (this.subscriptionManager.iframeMessenger && this.subscriptionManager.iframeMessenger.setClosed) {
        this.subscriptionManager.iframeMessenger.setClosed();
      }
    } catch (err) {
      log.error(err);
    }

    this.subscriptionManager.triggerEvent(Event.OPTIN_CLOSED);
  }

  onAllowClick(e) {
    logMethodCall('onAllowClick');

    e.preventDefault();

    const selectedTopics = this.getSelectedTopics();
    const hasTopics = this.hasTopicCheckboxes(true);

    if (isNewerEdge()) {
      this.hideBackdrop();
    }

    if (this.config.confirmAlertRequireChannelTopics && !selectedTopics.length && hasTopics && (this.withTopics || !this.config.confirmAlertSelectTopicsLater)) {
      alert(this.config.confirmAlertRequireChannelTopicsTitle || 'Bitte wähle mindestens eine Kategorie aus.');
      return;
    }

    if (typeof this.allowCallback === 'function') {
      this.allowCallback(true, selectedTopics);
      this.allowCallback = undefined;

      if (!this.withTopics) {
        this.hide();
        return;
      }
    }

    if (!this.withTopics && this.config.showConfirmAlert && this.config.confirmAlertDouble) {
      if (os.name === 'Android' || device.type === 'mobile' || (this.config.env === 'PREVIEW' && this.config.mobilePreview)) {
        this.hide();
        this.hideBackdrop();
        this.appendDoubleBox();

        // track the confirm of the first step
        this.subscriptionManager.api.confirmAlertShown(this.subscriptionManager.existingPermission, 'confirm');
        return;
      }
      const backdrop = document.querySelector(BACKDROP_SELECTOR);
      if (backdrop) {
        const arrowNode = this.getArrowNode('grant', {
          textNode: {},
          position: {
            x: window.innerWidth / 2,
            y: window.innerHeight / 2
          }
        });
        if (arrowNode) {
          backdrop.appendChild(arrowNode);
        }
      }
    }

    if (!this.confirmBox) {
      this.confirmBox = document.querySelector(`.${CONFIRM_CLASS_PREFIX}-${this.id}`);
    }
    if (this.confirmBox) {
      const topics = this.confirmBox.querySelectorAll('input[name="topics[]"]');
      for (let topicIndex = 0; topicIndex < topics.length; topicIndex += 1) {
        if (topics[topicIndex].checked && topics[topicIndex].dataset && topics[topicIndex].dataset.childTopicsRequired === 'true') {
          const childTopics = this.confirmBox.querySelectorAll(`.topic-child[data-parent-topic="${topics[topicIndex].value}"] input`);
          if (childTopics.length) {
            let checkedChildTopics = 0;
            for (let childTopicIndex = 0; childTopicIndex < childTopics.length; childTopicIndex += 1) {
              if (childTopics[childTopicIndex].checked) {
                checkedChildTopics += 1;
              }
            }
            if (checkedChildTopics === CHECKED_TOPICS_AMOUNT_NEEDED) {
              alert(`Bitte wähle mindestens eine Kategorie in ${(topics[topicIndex].parentNode.innerText || EMPTY_STRING).trim()} aus.`);
              return;
            }
          }
        }
      }
    }

    this.subscriptionManager.setTopics(selectedTopics);

    if (this.withTopics && this.config.confirmAlertSelectTopicsLater && hasTopics) {
      this.subscriptionManager.sync();
      this.withTopics = false;
      this.hide();
      if (this.subscribeCallback) {
        this.subscribeCallback(false);
      }

      if (this.subscriptionManager.registerForPushLater) {
        this.subscriptionManager.registerForPush();
      }

      this.subscriptionManager.triggerEvent(Event.TOPICS_MANUALLY_SAVED);

      return;
    }

    if (this.config.showUnblockTutorial) {
      this.hide();

      this.showBackdrop(undefined, 'denied', translate('popup.info').formatCleverPush(`<strong>${translate('confirm.allowBrowser') || translate('confirm.allow')}</strong>`));

      try {
        sessionStorage.setItem('cleverpush-subscription-status', 'denied');
      } catch (storageError) {
        // Ignore storage errors
      }

      return;
    }

    if (this.config.showSilentPromptTutorial) {
      this.hide();

      this.config.confirmAlertHideArrow = false;
      this.showBackdrop(undefined, 'silentPrompt', translate('confirm.silentPromptInfo'));
    }

    // show native confirm box
    let shouldAppendNativeBox = false;
    if (this.config.ownDomain && (window.parent.location.protocol === 'https:' || window.parent.location.hostname === 'localhost') && this.config.showConfirmAlert) {
      if (this.config.confirmAlertNativeTheme && this.config.confirmAlertNativeTheme !== 'cleverpush-confirm-default' && this.config.confirmAlertNativeTheme !== 'cleverpush-confirm-backdrop-text') {
        this.removeConfirmBox();
        shouldAppendNativeBox = true;
      } else {
        this.hide();
      }

      // track the confirm of the first step
      this.subscriptionManager.api.confirmAlertShown(this.subscriptionManager.existingPermission, 'confirm');
    } else {
      this.hide();
    }

    localStorage.setItem('cleverpush-subscription-status', 'allowed');

    if (this.config.env === 'PREVIEW') {
      const nativePreview = document.getElementById('cleverpush-preview-native');
      if (nativePreview) {
        nativePreview.style.display = 'block';

        if (shouldAppendNativeBox) {
          this.appendConfirmBox(`${this.config.confirmAlertNativeTheme} ${this.config.confirmAlertNativeTheme}-native`);
        }

        return;
      }
    }

    let subscribeCallbackCalled = false;
    let showBackdrop;

    detectPrompt({
      subscriptionManager: this.subscriptionManager,
      config: this.config,
      subscribe: () => {
        this.subscriptionManager.subscribeForce(false).then((res) => {
          subscribeCallbackCalled = true;
          this.hideBackdrop();
          if (this.subscribeCallback) {
            this.subscribeCallback(false, res);
          }
        }).catch((err) => {
          subscribeCallbackCalled = true;
          this.hideBackdrop();
          if (this.subscribeCallback) {
            this.subscribeCallback(err);
          }
        });
      },
      showBackdrop: () => {
        if (shouldAppendNativeBox) {
          this.appendConfirmBox(`${this.config.confirmAlertNativeTheme} ${this.config.confirmAlertNativeTheme}-native`);
        }
      },
      setShowBackdrop: (backdrop) => showBackdrop = backdrop,
      showSilentAlert: () => {
        if (!subscribeCallbackCalled && !this.config.silentPromptTutorialDisabled) {
          this.hide();

          this.config.confirmAlertHideArrow = false;
          this.showBackdrop(undefined, 'silentPrompt', translate('confirm.silentPromptInfo'));
        }
      },
    });

    this.subscriptionManager.triggerEvent(Event.OPTIN_ALLOWED);
  }

  appendDoubleBox() {
    const box = createNode(
      `
      <div class="${CONFIRM_CLASS_PREFIX}-double ${CONFIRM_CLASS_PREFIX}-double-${this.id}">
        <div class="${CONFIRM_CLASS_PREFIX}-double-above">
          ${translate('confirm.doubleText').formatCleverPush(`<strong>${translate('confirm.allowBrowser') || translate('confirm.allow')}</strong>`)}
        </div>
        <div class="${CONFIRM_CLASS_PREFIX}-double-content">
          <div class="${CONFIRM_CLASS_PREFIX}-double-text">
            <div class="${CONFIRM_CLASS_PREFIX}-double-bell">
              <svg height="512pt" viewBox="-21 0 512 512" width="512pt" xmlns="http://www.w3.org/2000/svg">
              <path d="m434.753906 360.8125c-32.257812-27.265625-50.753906-67.117188-50.753906-109.335938v-59.476562c0-75.070312-55.765625-137.214844-128-147.625v-23.042969c0-11.796875-9.558594-21.332031-21.332031-21.332031-11.777344 0-21.335938 9.535156-21.335938 21.332031v23.042969c-72.253906 10.410156-128 72.554688-128 147.625v59.476562c0 42.21875-18.496093 82.070313-50.941406 109.503907-8.300781 7.105469-13.058594 17.429687-13.058594 28.351562 0 20.589844 16.746094 37.335938 37.335938 37.335938h352c20.585937 0 37.332031-16.746094 37.332031-37.335938 0-10.921875-4.757812-21.246093-13.246094-28.519531zm0 0"/><path d="m234.667969 512c38.632812 0 70.953125-27.542969 78.378906-64h-156.757813c7.421876 36.457031 39.742188 64 78.378907 64zm0 0"/></svg>
            </div>
            <div class="${CONFIRM_CLASS_PREFIX}-double-title">${translate('confirm.nativeTitle').formatCleverPush(location.hostname || this.config.domain)}</div>
          </div>
          <div class="${CONFIRM_CLASS_PREFIX}-double-buttons">
            <button type="button" class="${CONFIRM_CLASS_PREFIX}-double-btn">${(translate('confirm.allowBrowser') || translate('confirm.allow'))}</button>
          </div>
        </div>
        <div class="${CONFIRM_CLASS_PREFIX}-double-below">
          <div class="${CONFIRM_CLASS_PREFIX}-double-arrow"></div>
        </div>
      </div>
      `
    );

    const content = box.querySelector('.cleverpush-confirm-double-content');
    const contentSize = content.getBoundingClientRect();

    const arrow = box.querySelector('.cleverpush-confirm-double-arrow');
    if (arrow) {
      const arrowNode = this.getArrowNode('own', {
        x: window.innerWidth - 55,
        y: (window.innerHeight / 2) + (contentSize.height / 2) + 65,
        padding: 0,
        textNode: box.querySelector('.cleverpush-confirm-double-below')
      });
      if (arrowNode) {
        arrow.appendChild(arrowNode);
      }
    }

    const allowButton = box.querySelector('.cleverpush-confirm-double-btn');
    if (allowButton) {
      allowButton.addEventListener('click', this.onDoubleAllowClick.bind(this));
    }

    document.body.appendChild(box);

    this.doubleBox = document.body.querySelector('.cleverpush-confirm-double');
  }

  onDoubleAllowClick(e) {
    e.preventDefault();
    logMethodCall('onDoubleAllowClick');

    if (this.doubleBox) {
      this.doubleBox.parentNode.removeChild(this.doubleBox);
    }

    this.subscriptionManager.subscribeForce(false).then((res) => {
      this.hideBackdrop();
      if (this.subscribeCallback) {
        this.subscribeCallback(false, res);
      }
    }).catch((err) => {
      this.hideBackdrop();
      if (this.subscribeCallback) {
        this.subscribeCallback(err);
      }
    });
  }

  /**
   * Triggered when user clicks on 'Deny' in confirm layer
   * @param {Event} e Click Event
   */
  onDenyClick(e) {
    logMethodCall('denyPush');

    e.preventDefault();

    try {
      if (this.config.env !== 'PREVIEW') {
        this.subscriptionManager.storageManager.setDenied();
        if (this.subscriptionManager.iframeMessenger && this.subscriptionManager.iframeMessenger.setDenied) {
          this.subscriptionManager.iframeMessenger.setDenied();
        }
      }
    } catch (err) {
      log.error(err);
    }

    this.autoHideBackdrop = true;
    this.hide();

    if (this.config.ownDomain && (window.parent.location.protocol === 'https:' || window.parent.location.hostname === 'localhost')) {
      this.showDenyAlert();
    }

    if (typeof this.allowCallback === 'function') {
      this.allowCallback(false, []);
      this.allowCallback = undefined;
    }

    this.subscriptionManager.triggerEvent(Event.OPTIN_DENIED);
  }

  /**
   * Triggered when user clicks on 'Deny' in topics layer
   * @param {Event} e Click Event
   */
  onTopicsDenyClick(e, topicId) {
    logMethodCall('denyPush');

    e.preventDefault();

    this.autoHideBackdrop = true;
    this.hide();

    if (typeof this.allowCallback === 'function') {
      this.allowCallback(false, []);
      this.allowCallback = undefined;
    }

    this.subscriptionManager.triggerEvent(Event.TOPICS_LAYER_DENIED, topicId);
  }

  onTopicsAllowClick(event, topicId) {
    this.onAllowClick(event);

    this.subscriptionManager.triggerEvent(Event.TOPICS_LAYER_ALLOWED, topicId);
  }

  hasTopicCheckboxes(internal) {
    logMethodCall('hasTopicCheckboxes');

    if (this.config.confirmAlertSelectTopicsLater && !this.withTopics && typeof internal === 'undefined') {
      // used to initially set all pre-checked topics
      return true;
    }

    const confirm = document.querySelector(CONFIRM_SELECTOR);
    if (confirm) {
      const checkboxes = confirm.querySelectorAll('input[name="topics[]"]');
      return checkboxes.length > 0;
    }

    return false;
  }

  getSelectedTopics() {
    logMethodCall('getSelectedTopics');

    // by default, return all pre-checked topics
    let topics = (this.channelTopics || []).filter(topicChecked).map((topic) => topic._id);

    const confirm = document.querySelector(CONFIRM_SELECTOR);
    if (confirm) {
      const checkboxes = confirm.querySelectorAll('input[name="topics[]"]');
      if (checkboxes && checkboxes.length) {
        topics = [];
        for (let i = 0; i < checkboxes.length; i += 1) {
          if ((checkboxes[i].type === 'checkbox' || checkboxes[i].type === 'radio') && checkboxes[i].checked) {
            topics.push(checkboxes[i].value);
          }
        }
      }
    }

    return topics;
  }

  showDenyAlert() {
    logMethodCall('showDenyAlert');
  }

  onClickToCloseDenyAlert(e) {
    if (this.denyAlert && e.target !== this.denyAlert && e.target.parentNode !== this.denyAlert) {
      document.removeEventListener('click', this.onClickToCloseDenyAlertListener);

      this.onDenyAlertClick();
    }
  }

  onDenyAlertClick(e) {
    if (typeof e !== 'undefined') {
      e.preventDefault();
    }

    logMethodCall('onDenyAlertClick');

    const elem = document.getElementsByClassName('cleverpush-deny-alert')[0];
    elem.parentNode.removeChild(elem);

    this.denyAlert = null;
  }

  showBrandingBox() {
    logMethodCall('showBrandingBox');

    document.body.appendChild(createNode(
      `<div class="cleverpush-branding-box cleverpush-branding-box-${this.id}"><div class="cleverpush-branding-box-text">${this.getBrandingLinks()}</div><button type="button" class="cleverpush-branding-box-close">&times;</button></div>`,
    ));

    this.brandingBox = document.querySelector(`.cleverpush-branding-box-${this.id}`);

    this.brandingBoxCloseButton = this.brandingBox.querySelector('.cleverpush-branding-box-close');
    if (this.brandingBoxCloseButton) {
      this.brandingBoxCloseButton.addEventListener('click', this.removeBrandingBox.bind(this));
    }
  }

  getBrandingLinks() {
    const links = [];

    const lc = this.config.confirmAlertLinkStyle?.color
      || this.config.confirmAlertThemeLinkColor;
    const brandingStyle = lc ? `style="color: ${lc} !important"` : '';

    if (!this.config.hideBranding) {
      let domain = this.config.cleverpushDomain || 'cleverpush.com';
      if (domain === 'mycleverpush.com') domain = 'cleverpush.com';

      const brand = 'CleverPush';

      const label = this.isNewOptIn(this.config.confirmAlertTheme) ? `<strong>${brand}</strong>` : brand;

      links.push(`<a href="https://${domain}/" target="_blank" ${brandingStyle} tabindex="0">Powered by ${label}</a>`);
    }

    [
      [this.config.privacyPolicyUrl, this.config.privacyPolicyText || translate('privacy')],
      [this.config.faqUrl, this.config.faqText || 'FAQ']
    ].forEach(([url, text]) => {
      if (url) {
        links.push(
          `<a href="${url}" target="_blank" ${brandingStyle} tabindex="0">${text}</a>`
        );
      }
    });

    return links.join(' &nbsp;/&nbsp; ');
  }

  showBackdrop(callback, typeParam, errorMessage) {
    logMethodCall('showBackdrop');

    let type = 'grant';
    if (typeof typeParam !== 'undefined') {
      if (typeParam === 'denied' || typeParam === 'silentPrompt') {
        type = typeParam;
      }
    }

    if (
      type !== 'denied'
      && type !== 'silentPrompt'
      && !this.config.hideNativeConfirmAlert
      && !this.config.showConfirmAlert
      && (
        this.config.confirmAlertNativeTheme
        && this.config.confirmAlertNativeTheme !== 'cleverpush-confirm-default'
        && this.config.confirmAlertNativeTheme !== 'cleverpush-confirm-backdrop-text'
      )
      && this.config.ownDomain
      && (window.parent.location.protocol === 'https:' || window.parent.location.hostname === 'localhost')
    ) {
      log.debug('appending native backdrop', this.config.showConfirmAlert);
      this.appendConfirmBox(`${this.config.confirmAlertNativeTheme} ${this.config.confirmAlertNativeTheme}-native`);
    }

    if (
      !this.config.confirmAlertBackdrop
      && !this.config.showConfirmAlert
      && (!this.config.confirmAlertNativeTheme || this.config.confirmAlertNativeTheme === 'cleverpush-confirm-default')
      && !this.config.hideBranding
    ) {
      this.showBrandingBox();
    }

    if (
      type !== 'denied'
      && type !== 'silentPrompt'
      && (typeof this.config.confirmAlertBackdrop !== 'undefined' && !this.config.confirmAlertBackdrop)
    ) {
      if (typeof callback === 'function') {
        callback();
      }
      return;
    }

    let configAlertLocalization = this.config.alertLocalization || {};
    const language = detectLanguage(this.config || {});
    if (this.config.alertLocalizationTranslations && language && this.config.alertLocalizationTranslations[language]) {
      configAlertLocalization = { ...configAlertLocalization, ...this.config.alertLocalizationTranslations[language] };
    }

    let backdropText = EMPTY_STRING;
    if (
      this.config.ownDomain
      && (window.parent.location.protocol === 'https:' || window.parent.location.hostname === 'localhost')
    ) {
      if (
        type === 'denied'
        || type === 'silentPrompt'
        || this.config.confirmAlertNativeTheme === 'cleverpush-confirm-backdrop-text'
      ) {
        if (type === 'grant') {
          backdropText += `<div class="cleverpush-backdrop-text" style="${this.config.confirmAlertThemeTextColor ? (`color: ${this.config.confirmAlertThemeTextColor}`) : EMPTY_STRING}">
${this.config.channelIcon && !this.config.confirmAlertHideChannelIcon ? `<p class="cleverpush-backdrop-icon"><img src="${this.config.channelIcon}" alt="Logo"></p>` : EMPTY_STRING}
<h2>${charsetEscape(configAlertLocalization.title) || translate('popup.info').formatCleverPush(`<strong>${translate('confirm.allowBrowser') || translate('confirm.allow')}</strong>`)}</h2>
<p>${charsetEscape(configAlertLocalization.info) || translate('popup.text').formatCleverPush(`<strong>${this.config.channelName}</strong>`)}</p>
</div>`;
        } else if (type === 'silentPrompt') {
          backdropText += `<div class="cleverpush-backdrop-text"><h2>${configAlertLocalization.silentPromptOptInInputText || errorMessage}</h2></div>`;
        } else {
          const parser = new UAParser();
          let { name: browser } = parser.getBrowser();
          let { name: platform } = parser.getOS();
          browser = browser.toLowerCase().replace(/\s/g, EMPTY_STRING).replace('chromium', 'chrome').replace('edge', 'chrome');
          platform = platform.toLowerCase().replace(/\s/g, EMPTY_STRING).replace('macos', 'mac');

          let unblockImage = EMPTY_STRING;
          if ((browser === 'chrome' || browser === 'firefox' || browser === 'safari') && (platform === 'android' || platform === 'windows' || platform === 'mac')) {
            if (browser === 'chrome' && platform === 'android') {
              unblockImage = `<div><video src="https://static.cleverpush.com/app/images/unblock-tutorial/${browser}-${platform}.mp4" width="100%" autoplay loop muted playsinline /></div>`;
            } else {
              unblockImage = `<div><img src="https://static.cleverpush.com/app/images/unblock-tutorial/${browser}-${platform}.gif" /></div>`;
            }
          }

          backdropText += `<div class="cleverpush-backdrop-text"><h2>${unblockImage ? translate('unblock.title') : errorMessage}</h2>${unblockImage}</div>`;
        }
      }
    }

    const existingBackdrops = document.getElementsByClassName('cleverpush-backdrop');
    if (existingBackdrops && existingBackdrops.length) {
      for (let i = 0; i < existingBackdrops.length; i += 1) {
        const existingBackdrop = existingBackdrops[i];
        if (existingBackdrop && existingBackdrop.parentNode) {
          existingBackdrop.parentNode.removeChild(existingBackdrop);
        }
      }
    }

    const backdrop = document.createElement('div');
    backdrop.className = `cleverpush-backdrop cleverpush-backdrop-${type}`;
    backdrop.style.display = 'none';
    backdrop.innerHTML = backdropText;

    if (this.config.confirmAlertBackdropOpacity && type !== 'denied' && type !== 'silentPrompt') {
      backdrop.style.backgroundColor = `rgba(0, 0, 0, ${this.config.confirmAlertBackdropOpacity})`;
    }
    if (this.config.confirmAlertBackdropBlur) {
      backdrop.className += ' cleverpush-backdrop-blur';
    }

    // remove backdrop on click
    if (!this.config.confirmAlertHideOnBackgroundClick) {
      backdrop.addEventListener('click', (e) => {
        e.preventDefault();
        this.hideBackdrop(type);
      });
    }

    if (document.body) {
      document.body.appendChild(backdrop);

      if (document.body.classList) {
        document.body.classList.add('has-cleverpush-backdrop');
        if (this.config.confirmAlertBackdropBlur) {
          document.body.classList.add('has-cleverpush-backdrop-blur');
        }
      } else {
        document.body.className += ' has-cleverpush-backdrop';
        if (this.config.confirmAlertBackdropBlur) {
          document.body.className += ' has-cleverpush-backdrop-blur';
        }
      }
    }

    if (typeof window.renderCustomToolTip === 'function' && backdropText) {
      window.renderCustomToolTip(backdrop);
      fadeIn(backdrop, 200, typeof callback !== 'undefined' ? callback : () => { });
      return;
    }

    if (backdropText && backdropText.length && !this.config.confirmAlertHideArrow) {
      const arrowNode = this.getArrowNode(type);
      if (arrowNode) {
        backdrop.appendChild(arrowNode);
      }
    }

    fadeIn(backdrop, 200, typeof callback !== 'undefined' ? callback : () => { });
  }

  hideBackdrop(permission) {
    logMethodCall('hideBackdrop');

    this.removeBrandingBox();

    if (document.body) {
      if (document.body.classList) {
        if (this.config.confirmAlertBackdropBlur) {
          document.body.classList.remove('has-cleverpush-backdrop-blur');
        }
        document.body.classList.remove('has-cleverpush-backdrop');
      } else {
        if (this.config.confirmAlertBackdropBlur) {
          document.body.className = document.body.className.replace('has-cleverpush-backdrop-blur', EMPTY_STRING);
        }
        document.body.className = document.body.className.replace('has-cleverpush-backdrop', EMPTY_STRING);
      }
    }

    if (
      !this.config.hideNativeConfirmAlert
      && (this.config.confirmAlertNativeTheme
        && this.config.confirmAlertNativeTheme !== 'cleverpush-confirm-default'
        && this.config.confirmAlertNativeTheme !== 'cleverpush-backdrop-text')
      && this.confirmBox
      && this.confirmBox.className.includes(`${this.config.confirmAlertNativeTheme}-native`)
    ) {
      this.removeConfirmBox();
    }

    if ((typeof permission === 'undefined' || permission !== 'denied') && typeof this.config.confirmAlertBackdrop !== 'undefined' && !this.config.confirmAlertBackdrop && !this.config.showSilentPromptTutorial) {
      return;
    }

    if (typeof this.bellResetCallback === 'function' && (typeof permission === 'undefined' || permission === 'grant')) {
      this.bellResetCallback(false);
      this.bellResetCallback = undefined;
    }

    const backdrops = document.getElementsByClassName('cleverpush-backdrop');
    for (let backdropIndex = 0; backdropIndex < backdrops.length; backdropIndex += 1) {
      const backdrop = backdrops[backdropIndex];
      ((backdropNode) => {
        fadeOut(backdropNode, 200, () => {
          if (backdropNode && backdropNode.parentNode) {
            backdropNode.parentNode.removeChild(backdropNode);
          }
        });
      })(backdrop);
    }
  }

  getLocalization(confirmConfig) {
    let configAlertLocalization = this.config.alertLocalization || {};
    const language = detectLanguage(this.config || {});
    if (this.config.alertLocalizationTranslations && language && this.config.alertLocalizationTranslations[language]) {
      configAlertLocalization = { ...configAlertLocalization, ...this.config.alertLocalizationTranslations[language] };
    }

    configAlertLocalization = { ...configAlertLocalization, ...confirmConfig.customLocalization };

    if (confirmConfig.topicsRequired) {
      this.config.confirmAlertRequireChannelTopics = true;
    }

    const localization = {
      title: charsetEscape(configAlertLocalization.title) || translate('confirm.title').formatCleverPush(this.config.channelName),
      info: charsetEscape(configAlertLocalization.info) || translate('confirm.info'),
      deny: charsetEscape(configAlertLocalization.deny) || translate('confirm.deny'),
      allow: charsetEscape(configAlertLocalization.allow) || translate('confirm.allow'),
      confirmInfo: charsetEscape(configAlertLocalization.confirmInfo) || translate('popup.info').formatCleverPush(translate('confirm.allowBrowser')),
      nativeTitle: charsetEscape(configAlertLocalization.nativeTitle),
      nativeText: charsetEscape(configAlertLocalization.nativeText),
      moreInfo: charsetEscape(configAlertLocalization.moreInfo),
      moreButton: charsetEscape(configAlertLocalization.moreButton) || translate('confirm.more'),
    };

    return localization;
  }

  async appendConfirmBox(confirmAlertThemeParam, withTopicsParam, configParam, allowCallback) {
    logMethodCall('appendConfirmBox', confirmAlertThemeParam, withTopicsParam, configParam);

    if (this.confirmBox) {
      // already appended
      return;
    }

    this.withTopics = typeof withTopicsParam === 'boolean' && withTopicsParam;

    if (this.withTopics && document.querySelector('.cleverpush-topic-box')) {
      // already appended
      return;
    }

    if (typeof allowCallback === 'function') {
      this.allowCallback = allowCallback;
    }

    const confirmConfig = typeof configParam === 'object' ? configParam : {};

    const localization = this.getLocalization(confirmConfig);

    let confirmClass = `${CONFIRM_CLASS_PREFIX}-default`;
    let confirmStyle = EMPTY_STRING;
    const confirmAlertTheme = typeof confirmAlertThemeParam !== 'undefined' ? confirmAlertThemeParam : this.config.confirmAlertTheme;
    if (confirmAlertTheme) {
      const isNewOptInAvailable = this.isNewOptIn(confirmAlertTheme);
      if (isNewOptInAvailable) {
        await import(`./styles/${confirmAlertTheme}.scss`);
      }
      confirmClass = confirmAlertTheme;
    }
    const parser = new UAParser();
    if (!this.config.confirmAlertThemeVariant || this.config.confirmAlertThemeVariant === 'cleverpush-confirm-auto') {
      if (parser.getOS().name === 'Mac OS') {
        confirmClass += ` ${CONFIRM_CLASS_PREFIX}-mac`;
      } else if (parser.getOS().name === 'Windows') {
        confirmClass += ` ${CONFIRM_CLASS_PREFIX}-windows ${CONFIRM_CLASS_PREFIX}-chrome`;
      }
    } else {
      confirmClass += ` ${this.config.confirmAlertThemeVariant}`;
    }

    if (parser.getBrowser().name === 'Firefox') {
      confirmClass += ` ${CONFIRM_CLASS_PREFIX}-firefox`;
    }
    if (parser.getBrowser().name === 'Opera') {
      confirmClass += ` ${CONFIRM_CLASS_PREFIX}-opera`;
    }
    if (parser.getBrowser().name === 'Safari') {
      confirmClass += ` ${CONFIRM_CLASS_PREFIX}-safari`;
    }
    if (parser.getBrowser().name === 'Chrome' || (parser.getBrowser().name === 'Edge' && isNewerEdge())) {
      confirmClass += ` ${CONFIRM_CLASS_PREFIX}-chrome`;
    } else if (parser.getBrowser().name === 'Edge') {
      confirmClass += ` ${CONFIRM_CLASS_PREFIX}-edge`;
    }

    // hotfix
    if (this.config.confirmAlertStyle && this.config.confirmAlertStyle.color) {
      this.config.confirmAlertThemeTextColor = this.config.confirmAlertStyle.color;
    }

    if (this.config.confirmAlertThemeBackgroundColor) {
      confirmStyle += `background-color: ${this.config.confirmAlertThemeBackgroundColor};`;
      if (this.config.confirmAlertThemeTextColor) {
        confirmStyle += `color: ${this.config.confirmAlertThemeTextColor};`;
      }
    } else if (this.config.confirmAlertThemeBackground) {
      confirmClass += (` ${this.config.confirmAlertThemeBackground}`);
    }

    if (this.config.confirmAlertHideChannelIcon) {
      confirmClass += ` ${CONFIRM_CLASS_PREFIX}-hide-channel-icon`;
    }
    if (this.config.confirmAlertHideArrow) {
      confirmClass += ` ${CONFIRM_CLASS_PREFIX}-hide-arrow`;
    }

    if (this.config.env === 'PREVIEW') {
      confirmClass += ` ${CONFIRM_CLASS_PREFIX}-preview`;
    }

    if (this.config.confirmAlertCloseButton) {
      confirmClass += ' cleverpush-confirm-with-close';
    }

    if (this.config.confirmAlertPlacement) {
      confirmClass += ` ${this.config.confirmAlertPlacement}`;
    }

    // add id to confirm class
    confirmClass += ` ${CONFIRM_CLASS_PREFIX}-${this.id}`;

    let topicSelection = EMPTY_STRING;
    if (!confirmConfig.topicsLayer || this.withTopics) {
      if (this.config.topicsTemplateFunction) {
        topicSelection = `<div class="cleverpush-topics ${CONFIRM_CLASS_PREFIX}-topics"></div>`;
      } else if (this.channelTopics && this.channelTopics.length && !this.config.confirmAlertHideChannelTopics) {
        let topics = this.channelTopics.filter((topic) => !topic.parentTopic);
        if (confirmConfig && confirmConfig.parentTopic) {
          topics = this.channelTopics.filter((topic) => topic.parentTopic === confirmConfig.parentTopic);
        }

        topicSelection += `<div class="cleverpush-topics ${CONFIRM_CLASS_PREFIX}-topics">`;
        topics.sort((a, b) => a.sort - b.sort).forEach((topic) => {
          const topicName = charsetEscape(topic.name);
          const topicDescription = topic.description ? `<div class="cleverpush-topic-description">${charsetEscape(topic.description)}</div>` : EMPTY_STRING;
          const childTopics = this.channelTopics.filter((childTopic) => childTopic.parentTopic === topic._id);

          topicSelection += `<div><div class="${childTopics.length ? 'topic-has-children' : EMPTY_STRING}"><label style="${this.config.confirmAlertThemeTextColor ? (`color: ${this.config.confirmAlertThemeTextColor};`) : EMPTY_STRING}" ><input type="${this.config.confirmAlertRadioButtonTopics || confirmConfig.topicsRadioButtons ? 'radio' : 'checkbox'}" name="topics[]" value="${topic._id}" tabindex="0" ${topicChecked(topic, this.subscribedTopics) ? 'checked' : EMPTY_STRING} ${topic.childTopicsRequired ? ' data-child-topics-required="true"' : EMPTY_STRING}><div>${topicName}${topicDescription}</div></label></div></div>`;
          if (childTopics.length) {
            childTopics.forEach((childTopic) => {
              const childTopicName = charsetEscape(childTopic.name);
              const childInputType = this.config.confirmAlertRadioButtonTopics || topic.childTopicsRadioButtons ? 'radio' : 'checkbox';
              topicSelection += `<div class="topic-child" data-parent-topic="${topic._id}" style="margin-left: 20px; font-size: 90%; ${!topicChecked(topic, this.subscribedTopics) ? 'display: none;' : EMPTY_STRING}"><label><input type="${childInputType}" name="topics[]" value="${childTopic._id}" tabindex="0" ${topicChecked(topic, this.subscribedTopics) && topicChecked(childTopic, this.subscribedTopics) ? 'checked' : EMPTY_STRING}> ${childTopicName}</label></div>`;
            });
          }
        });
        topicSelection += '</div>';
      }
    }

    let parentContainer;
    if (this.config.appendConfirmBoxTo) {
      parentContainer = document.querySelector(this.config.appendConfirmBoxTo);
    }
    if (!parentContainer) {
      if (this.config.appendConfirmBoxTo) {
        log.error('appendConfirmBoxTo: container not found. Falling back to default.');
      }
      parentContainer = document.body;
    } else {
      confirmClass += ' cleverpush-confirm-static';
    }

    const showLinks = true;
    if (!this.withTopics && (window.parent.location.protocol === 'https:' || window.parent.location.hostname === 'localhost')) {
      let isNativeBox = (
        confirmAlertThemeParam
        && confirmAlertThemeParam !== `${CONFIRM_CLASS_PREFIX}-default ${CONFIRM_CLASS_PREFIX}-default-native`
        && confirmAlertThemeParam !== `${CONFIRM_CLASS_PREFIX}-backdrop-text ${CONFIRM_CLASS_PREFIX}-backdrop-text-native`
      );
      // if user does not need to accept native browser prompt -> show topics in first step
      if (!isNativeBox && typeof window.Notification !== 'undefined' && window.Notification.permission === 'granted') {
        isNativeBox = true;
      }
      log.debug('isNativeBox', isNativeBox);

      if (this.config.confirmAlertSelectTopicsLater) {
        topicSelection = EMPTY_STRING;
      } else if (this.config.showConfirmAlert && this.config.isSafari_12_1) {
        if (isNativeBox) {
          topicSelection = EMPTY_STRING;
        }
      } else if (this.config.showConfirmAlert) {
        const canHideTopics = this.config.confirmAlertNativeTheme && this.config.confirmAlertNativeTheme !== 'cleverpush-confirm-default';
        if (isNativeBox) {
          // links were already shown on first confirm box
          // showLinks = false;

          if (canHideTopics && (this.config.showConfirmAlertMobile || this.config.confirmAlertSelectTopicsLaterDisabled)) {
            // show topics only on 1st box (mobile)
            topicSelection = EMPTY_STRING;
          }
        } else if (canHideTopics && !this.config.showConfirmAlertMobile && !this.config.confirmAlertSelectTopicsLaterDisabled) {
          // show topics only on 2nd box (desktop)
          topicSelection = EMPTY_STRING;
        }
      } else if (isNativeBox && this.config.confirmAlertSelectTopicsLater && this.config.ownDomain) {
        topicSelection = EMPTY_STRING;
      }

      if (isNativeBox) {
        localization.title = localization.nativeTitle || localization.title || EMPTY_STRING;
        localization.info = localization.nativeText || localization.info || EMPTY_STRING;
        localization.moreInfo = localization.moreInfo || EMPTY_STRING;
      }
    }

    if (this.facebookEnabled && !this.config.facebookButtonOptIn) {
      if (this.config.facebookDoubleOptIn && !this.facebookDoubleOptInConfirmed) {
        // add the facebook confirm checkbox to topic selection (if double optin enabled)
        topicSelection += `
        <div style="margin-top: 15px; margin-bottom: 0; font-weight: bold;" class="${CONFIRM_CLASS_PREFIX}-messenger-title cleverpush-topics ${CONFIRM_CLASS_PREFIX}-topics">Facebook Messenger</div>
        <div class="cleverpush-topics ${CONFIRM_CLASS_PREFIX}-topics"><div style="margin-top: 5px;"><label style="line-height: 1.5;"><input type="checkbox" id="cleverpush-facebook-checkbox"> Ich möchte den Facebook-Newsletter und akzeptiere die <a href="${this.config.privacyPolicyUrl}" target="_blank">Datenschutzerklärung</a></label></div></div>`;
      }

      // add the messenger checkbox to topic selection
      topicSelection += `<div class="fb-messenger-checkbox-wrap"><div class="fb-messenger-checkbox" origin="${location.origin}" page_id="${this.config.facebookPageId}" messenger_app_id="${this.config.facebookAppId}" user_ref="${this.facebookUserRef}" prechecked="true" allow_login="true" size="large" style="margin-top: 10px; margin-left: -14px;"></div></div>`;
    }

    if (this.withTopics && !confirmConfig.customLocalization) {
      confirmClass += ' cleverpush-topic-box';
      localization.title = charsetEscape(this.config.confirmAlertSelectTopicsLaterTitle) || translate('confirm.selectTopics') || localization.title;
      localization.info = EMPTY_STRING;
      localization.allow = this.config.confirmAlertSelectTopicsSaveText || translate('panel.save');
    }

    const infoStyle = `${this.config.confirmAlertThemeTextColor ? (`color: ${this.config.confirmAlertThemeTextColor};`) : EMPTY_STRING} ${this.config.confirmAlertStyle && this.config.confirmAlertStyle.fontSize ? (`font-size: ${this.config.confirmAlertStyle.fontSize}px !important;`) : EMPTY_STRING}`;
    const brandingLinks = showLinks ? `<div class="cleverpush-confirm-branding"><span>${this.getBrandingLinks()}</span></div>` : EMPTY_STRING;
    const topbarArrow = confirmAlertThemeParam === 'cleverpush-confirm-topbar cleverpush-confirm-topbar-native' ? '<div class="cleverpush-confirm-topbar-arrow"></div>' : EMPTY_STRING;
    const closeButtonLabel = translate('confirm.close');
    const closeButton = this.config.confirmAlertCloseButton ? `<button type="button" class="cleverpush-confirm-hide" aria-label="${closeButtonLabel}" tabindex="0">
    <svg class="svg-cross-style" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 6l12 12m0-12L6 18"/>
     </svg>
  </button>` : EMPTY_STRING;
    const iconClass = `cleverpush-confirm-icon${this.config.confirmAlertRoundChannelIcon ? ' cleverpush-confirm-icon-round' : EMPTY_STRING}`;
    const textStyle = this.config.confirmAlertThemeTextColor ? (`color: ${this.config.confirmAlertThemeTextColor};`) : EMPTY_STRING;
    const titleStyle = this.config.confirmAlertThemeTitleColor ? (`color: ${this.config.confirmAlertThemeTitleColor};`) : EMPTY_STRING;
    const allowButtonStyleInline = `${this.config.confirmAlertThemeButtonTextColor ? (`color: ${this.config.confirmAlertThemeButtonTextColor};`) : EMPTY_STRING} ${this.config.confirmAlertThemeButtonBackgroundColor ? (`border-color: transparent !important; background: none; background-color: ${this.config.confirmAlertThemeButtonBackgroundColor};`) : EMPTY_STRING}`;
    const automaticDarkMode = !!this.config.automaticDarkModeSupportPopups;

    let moreInfo = EMPTY_STRING;
    if (this.config.alertMoreInformationEnabled && localization.moreInfo && localization.moreButton) {
      let linkColor = this.config.confirmAlertThemeLinkColor;
      if (this.config.confirmAlertLinkStyle && this.config.confirmAlertLinkStyle.color) {
        linkColor = this.config.confirmAlertLinkStyle.color;
      }
      moreInfo = `
      <div style="display: none" class="cleverpush-confirm-more-text cleverpush-confirm-info">
        ${localization.moreInfo}
      </div>
      <div class="cleverpush-confirm-branding">
        <a href="#" style="${linkColor ? (`color: ${linkColor} !important`) : EMPTY_STRING}" class="cleverpush-confirm-more-btn">${localization.moreButton}</a>
      </div>
      `;
    }
    const allowButton = `
      <button type="button" tabindex="0" class="${CONFIRM_CLASS_PREFIX}-btn ${CONFIRM_CLASS_PREFIX}-btn-allow" style="${allowButtonStyleInline}">
        ${localization.allow}
      </button>
    `;

    const denyButton = `
      <button type="button" tabindex="0" class="${CONFIRM_CLASS_PREFIX}-btn ${CONFIRM_CLASS_PREFIX}-btn-deny">
        ${localization.deny}
      </button>
    `;

    const footerButtons = parser.getOS().name === WINDOWS
      ? `${allowButton}${denyButton}`
      : `${denyButton}${allowButton}`;

    const confirmAlertHeroImage = this.config.confirmAlertHeroImage && confirmAlertTheme === CLEVER_OPT_IN
      ? `<div class="hero-image" style="background-image:url(${this.config.confirmAlertHeroImage})"></div>` : EMPTY_STRING;
    const confirm = createNode(`
      <div class="${CONFIRM_CLASS_PREFIX} ${confirmClass}" style="${confirmStyle}" role="alertdialog" aria-modal="true" aria-live="assertive" aria-labelledby="confirmTitle" aria-describedby="confirmInfo" tabindex="-1">
        <div class="${CONFIRM_CLASS_PREFIX}-caret ${CONFIRM_CLASS_PREFIX}-caret-top" style="display: none; border-bottom-color: ${this.config.confirmAlertThemeBackgroundColor}"></div>
        <div class="${CONFIRM_CLASS_PREFIX}-caret ${CONFIRM_CLASS_PREFIX}-caret-bottom" style="display: none; border-top-color: ${this.config.confirmAlertThemeBackgroundColor}; ${confirmAlertTheme === `${CONFIRM_CLASS_PREFIX}-vortex-notification` && this.config.confirmAlertAllowButtonStyle.backgroundColor ? `background-color: ${this.config.confirmAlertAllowButtonStyle.backgroundColor}` : ''}"></div>
        ${closeButton}
        <div class="${CONFIRM_CLASS_PREFIX}-wrap">
          ${topbarArrow}
          <div class="${iconClass}"><img src="${this.config?.darkModeIcon && automaticDarkMode ? this.config.darkModeIcon : this.config.channelIcon}" alt="logo"></div>
          ${confirmAlertHeroImage}
          <div class="${CONFIRM_CLASS_PREFIX}-text" style="${textStyle}">
            ${this.isNewOptIn(confirmAlertTheme) ? '<hr/>' : EMPTY_STRING}
            ${confirmAlertTheme === `${CONFIRM_CLASS_PREFIX}-swift-alert` ? `<p class="${CONFIRM_CLASS_PREFIX}-swift-alert-main-title">${translate('confirm.swiftAlertTitle')}</p>` : ''}
            <span class="${CONFIRM_CLASS_PREFIX}-title" style="${titleStyle}" id="confirmTitle">${localization.title}</span>
            <p class="${CONFIRM_CLASS_PREFIX}-info" style="${infoStyle}" id="confirmInfo">${localization.info}</p>
            ${moreInfo}
            ${brandingLinks}
            ${topicSelection}
          </div>
          <div class="cleverpush-clearfix cleverpush-clearfix-bottom">
            <div class="${CONFIRM_CLASS_PREFIX}-buttons ${(!this.config.hideBranding || this.config.privacyPolicyUrl || (this.config.faqText && this.config.faqUrl)) ? `${CONFIRM_CLASS_PREFIX}-padding-bottom` : ''}">
            ${footerButtons}
            </div>
            <div class="cleverpush-clearfix"></div>
          </div>
          ${localization.confirmInfo ? `
            <div class="${CONFIRM_CLASS_PREFIX}-bottom-info" style="display: none;">
              <div class="${iconClass}"><img src="${this.config?.darkModeIcon && automaticDarkMode ? this.config.darkModeIcon : this.config.channelIcon}"></div>
              <div class="${CONFIRM_CLASS_PREFIX}-bottom-info-text">${localization.confirmInfo}</div>
              <div class="cleverpush-clearfix"></div>
            </div>
          ` : EMPTY_STRING}
        </div>
        ${this.isNewOptIn(this.config.confirmAlertTheme) ? '<div class="cleverpush-fix" style="display: none"></div>' : EMPTY_STRING}
      </div>
    `);

    parentContainer.appendChild(confirm);

    let shouldApplyDarkMode = false;

    this.confirmBox = document.querySelector(`.${CONFIRM_CLASS_PREFIX}-${this.id}`);

    if (automaticDarkMode) {
      let backgroundColor = null;

      if (this.config.confirmAlertThemeBackgroundColor) {
        backgroundColor = this.config.confirmAlertThemeBackgroundColor;
      } else if (this.config.confirmAlertStyle && this.config.confirmAlertStyle.backgroundColor) {
        backgroundColor = this.config.confirmAlertStyle.backgroundColor;
      } else if (this.config.confirmAlertThemeBackground) {
        const computedStyle = window.getComputedStyle(this.confirmBox);
        backgroundColor = computedStyle.backgroundColor;
      } else {
        const computedStyle = window.getComputedStyle(this.confirmBox);
        backgroundColor = computedStyle.backgroundColor;

        if ((backgroundColor === 'transparent' || backgroundColor === 'rgba(0, 0, 0, 0)')) {
          const wrap = this.confirmBox.querySelector('.cleverpush-confirm-wrap');
          if (wrap) {
            const wrapStyle = window.getComputedStyle(wrap);
            backgroundColor = wrapStyle.backgroundColor;
          }
        }
      }

      shouldApplyDarkMode = this.isLightColor(backgroundColor);
    }

    if (shouldApplyDarkMode) {
      document.body.classList.add('automaticDarkModeSupport');
    } else {
      document.body.classList.remove('automaticDarkModeSupport');
    }

    this.confirmBox = document.querySelector(`.${CONFIRM_CLASS_PREFIX}-${this.id}`);
    if (this.confirmBox && typeof this.config.confirmAlertStyle === 'object') {
      for (const property in this.config.confirmAlertStyle) {
        if (this.config.confirmAlertStyle.hasOwnProperty(property)) {
          this.confirmBox.style[property] = this.config.confirmAlertStyle[property] + (!isNaN(this.config.confirmAlertStyle[property]) ? 'px' : EMPTY_STRING);
        }
      }
    }

    this.confirmBoxTitle = document.querySelector(`.${CONFIRM_CLASS_PREFIX}-${this.id} .${CONFIRM_CLASS_PREFIX}-title`);
    if (this.confirmBoxTitle && typeof this.config.confirmAlertTitleStyle === 'object') {
      for (const property in this.config.confirmAlertTitleStyle) {
        if (this.config.confirmAlertTitleStyle.hasOwnProperty(property)) {
          this.confirmBoxTitle.style[property] = this.config.confirmAlertTitleStyle[property] + (!isNaN(this.config.confirmAlertTitleStyle[property]) ? 'px' : EMPTY_STRING);
        }
      }
    }

    // if only button background color is set OR button background color === allow button background color:
    // -> unset button background color and set allow button background color
    try {
      const buttonStyle = this.config.confirmAlertButtonStyle || {};
      const allowButtonStyle = this.config.confirmAlertAllowButtonStyle || {};
      if (
        buttonStyle.backgroundColor
        && (
          !allowButtonStyle.backgroundColor
          || (this.config.confirmAlertAllowButtonStyle || {}).backgroundColor === buttonStyle.backgroundColor
        )
      ) {
        (this.config.confirmAlertAllowButtonStyle || {}).backgroundColor = buttonStyle.backgroundColor;
        delete buttonStyle.backgroundColor;
      }
      if (
        buttonStyle.color
        && (
          !allowButtonStyle.color
          || (this.config.confirmAlertAllowButtonStyle || {}).color === buttonStyle.color
        )
      ) {
        (this.config.confirmAlertAllowButtonStyle || {}).color = buttonStyle.color;
        delete (this.config.confirmAlertButtonStyle || {}).color;
      }
    } catch (ignored) { }

    // fix margin left
    if (
      (this.cleverpush.config.customCss || EMPTY_STRING)
        .replace(REGEX_NEW_LINES, EMPTY_STRING)
        .replace(REGEX_SPACE, EMPTY_STRING)
        .includes(
          `.${CONFIRM_CLASS_PREFIX}-btn { width: 100% !important; }`
            .replace(REGEX_NEW_LINES, EMPTY_STRING)
            .replace(REGEX_SPACE, EMPTY_STRING)
        )
    ) {
      if (!this.config.confirmAlertAllowButtonStyle) {
        this.config.confirmAlertAllowButtonStyle = {};
      }
      this.config.confirmAlertAllowButtonStyle.marginLeft = '0px';
    }

    if (Object.keys(this.config.confirmAlertIconStyle || {}).length) {
      const icon = this.confirmBox.querySelector(`.${CONFIRM_CLASS_PREFIX}-icon img`);
      const style = this.config.confirmAlertIconStyle;
      if (icon) {
        if (style.width) {
          icon.style.width = `${style.width}px`;
          if (window.innerWidth >= 768) {
            const text = this.confirmBox.querySelector(`.${CONFIRM_CLASS_PREFIX}-text`);
            if (text) {
              const margin = 15;
              text.style.setProperty('width', `calc(100% - ${style.width + margin}px)`, 'important');
            }
          }
        }
        if (style.height) {
          icon.style.height = `${style.height}px`;
        }
      }
    }

    this.allowButton = this.confirmBox.querySelector(`.${CONFIRM_CLASS_PREFIX}-btn-allow`);
    if (this.allowButton) {
      if (confirmConfig.topicsLayer) {
        this.allowButton.addEventListener('click', (event) => this.onTopicsAllowClick(event, confirmConfig.parentTopic), true);
      } else {
        this.allowButton.addEventListener('click', this.onAllowClick.bind(this), true);
      }
      if (typeof this.config.confirmAlertButtonStyle === 'object') {
        if (this.config.confirmAlertButtonStyle.backgroundColor && !this.config.confirmAlertButtonStyle.borderColor) {
          this.config.confirmAlertButtonStyle.borderColor = 'transparent';
        }
        for (const property in this.config.confirmAlertButtonStyle) {
          if (this.config.confirmAlertButtonStyle.hasOwnProperty(property)) {
            this.allowButton.style.setProperty(camelCaseToDash(property), this.config.confirmAlertButtonStyle[property], 'important');
          }
        }
      }
      if (typeof this.config.confirmAlertAllowButtonStyle === 'object') {
        for (const property in this.config.confirmAlertAllowButtonStyle) {
          if (this.config.confirmAlertAllowButtonStyle.backgroundColor && !this.config.confirmAlertAllowButtonStyle.borderColor) {
            this.config.confirmAlertAllowButtonStyle.borderColor = 'transparent';
          }
          if (this.config.confirmAlertAllowButtonStyle.hasOwnProperty(property)) {
            this.allowButton.style.setProperty(camelCaseToDash(property), this.config.confirmAlertAllowButtonStyle[property], 'important');
          }
        }
      }
    } else {
      log.warn('allowButton not found', this.confirmBox);
    }

    this.denyButton = this.confirmBox.querySelector(`.${CONFIRM_CLASS_PREFIX}-btn-deny`);
    if (this.denyButton) {
      if (confirmConfig.topicsLayer) {
        this.denyButton.addEventListener('click', (event) => this.onTopicsDenyClick(event, confirmConfig.parentTopic));
      } else {
        this.denyButton.addEventListener('click', this.onDenyClick.bind(this));
      }
      if (typeof this.config.confirmAlertButtonStyle === 'object') {
        for (const property in this.config.confirmAlertButtonStyle) {
          if (this.config.confirmAlertButtonStyle.hasOwnProperty(property)) {
            this.denyButton.style.setProperty(camelCaseToDash(property), this.config.confirmAlertButtonStyle[property], 'important');
          }
        }
      }
    }

    this.closeButton = this.confirmBox.querySelector(`.${CONFIRM_CLASS_PREFIX}-hide`);
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.onCloseClick.bind(this));

      if (this.confirmBox && typeof this.config.confirmAlertStyle === 'object' && this.config.confirmAlertStyle.color) {
        this.closeButton.style.color = this.config.confirmAlertStyle.color;
      }
    }

    this.moreButton = this.confirmBox.querySelector(`.${CONFIRM_CLASS_PREFIX}-more-btn`);
    if (this.moreButton) {
      const moreDiv = this.confirmBox.querySelector(`.${CONFIRM_CLASS_PREFIX}-more-text`);
      if (moreDiv) {
        this.moreButton.addEventListener('click', (event) => {
          event.preventDefault();
          moreDiv.style.display = moreDiv.style.display === 'block' ? 'none' : 'block';
        });
      }
    }

    const topics = this.confirmBox.querySelectorAll('input[name="topics[]"]');
    for (let topicIndex = 0; topicIndex < topics.length; topicIndex += 1) {
      topics[topicIndex].addEventListener('change', (e) => {
        if (!this.confirmBox) {
          this.confirmBox = document.querySelector(`.${CONFIRM_CLASS_PREFIX}-${this.id}`);
        }
        const childTopics = this.confirmBox.querySelectorAll(`.topic-child[data-parent-topic="${e.target.value}"]`);
        for (let childTopicIndex = 0; childTopicIndex < childTopics.length; childTopicIndex += 1) {
          childTopics[childTopicIndex].style.display = e.target.checked ? 'block' : 'none';
          const childTopicInput = childTopics[childTopicIndex].querySelector('input[type="checkbox"], input[type="radio"]');

          const childTopic = (this.config.channelTopics || []).find((topic) => topic._id === childTopicInput.value);
          if (!e.target.checked) {
            childTopicInput.checked = false;
          } else if (childTopic) {
            childTopicInput.checked = topicChecked(childTopic, this.subscribedTopics);
          }
        }
      });
    }

    if (this.config.topicsTemplateFunction && topicSelection) {
      const topicsDiv = parentContainer.querySelector(`.${CONFIRM_CLASS_PREFIX} .${CONFIRM_CLASS_PREFIX}-topics`);
      if (topicsDiv) {
        try {
          const templateFunc = eval(`(${this.config.topicsTemplateFunction})`);
          if (typeof templateFunc === 'function') {
            log.debug('calling templateFunc');
            this.subscriptionManager.getTopics().then((selectedTopicIds) => {
              templateFunc(topicsDiv, this.config, selectedTopicIds, 'confirm');
            });
          } else {
            log.warn('typeof templateFunc is not function');
          }
        } catch (err) {
          log.warn(err);
        }
      } else {
        log.warn('.cleverpush-topics div not found');
      }
    }

    createdBy = document.activeElement;

    // --- Sentinel focus trap for cross-browser compatibility (Safari & Chrome) ---
    // Use shared utility for sentinels
    if (this._removeSentinelTrap) {
      this._removeSentinelTrap();
    }
    this._removeSentinelTrap = addSentinelFocusTrap(this.confirmBox);

    // Keydown handler for Escape to close the modal (no tab handling here)
    this.confirmBox.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.hideBackdrop();
        this.hide();
        event.preventDefault();
      }
    });

    // Remove sentinels when modal is closed
    if (!this._hideWrapped) {
      // Capture the original hide method in a closure to prevent multiple wrapping
      const originalHide = this.hide.bind(this);
      this.hide = (force = false) => {
        if (this._removeSentinelTrap) {
          this._removeSentinelTrap();
        }
        originalHide(force);
      };
      this._hideWrapped = true;
    }
  }

  show(subscribeCallback) {
    logMethodCall('showConfirmBox');

    if (typeof subscribeCallback === 'function') {
      this.subscribeCallback = subscribeCallback;
    }

    if (this.visible) {
      return;
    }
    this.visible = true;

    this.appendConfirmBox().then(() => {
      this.showBackdrop();
      if (this.confirmBox) {
        this.confirmBox.focus();
      }
    });
  }

  setAutoHideBackdrop(autoHideBackdrop) {
    this.autoHideBackdrop = autoHideBackdrop;
  }
}
